import React from 'react';
import { formatDate, DATE_FORMAT } from '@utils/date';
import PropTypes from 'prop-types';
import { Link, browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { Translate, I18n } from 'react-redux-i18n';

import { logout as actionLogout } from '../actions/auth';
import { cancelRecurrent as actionCancelRecurrent } from '../actions/user';

import actionChangeLang from '../actions/i18n';

import Navbar from 'react-bootstrap/lib/Navbar';
import NavItem from 'react-bootstrap/lib/NavItem';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import NavDropdown from 'react-bootstrap/lib/NavDropdown';
import { default as BaseNav } from 'react-bootstrap/lib/Nav';
import { LinkContainer } from 'react-router-bootstrap';

import SubscribitionReminder from './SubscribitionReminder';
import { isSubsValid } from '../actions/utils';

const StackedIcon = ({ icon }) => (
  <span className="fa-stack">
    <i className="fa fa-circle fa-stack-2x" style={{ color: '#99C93E' }} />
    <i className={`fa fa-${icon} fa-stack-1x`} style={{ color: 'white' }} />
  </span>
);

const LANGS = ['en', 'ru'];

const LangSwitch = ({ locale, changeLang, variant = 'inline' }) => {
  if (variant === 'dropdown') {
    return (
      <div
        className="lang-switch lang-switch--dropdown"
        onClick={e => e.stopPropagation()}
      >
        <span className="lang-switch__label">Язык / Language</span>
        <div className="lang-switch__buttons">
          {LANGS.map(lang => (
            <button
              key={lang}
              type="button"
              className={`lang-switch__btn${
                locale === lang ? ' lang-switch__btn--active' : ''
              }`}
              onClick={changeLang(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <span className="lang-switch lang-switch--inline">
      {LANGS.map((lang, index) => (
        <React.Fragment key={lang}>
          {index > 0 && <span className="lang-switch__sep">|</span>}
          <button
            type="button"
            className={`lang-switch__btn${
              locale === lang ? ' lang-switch__btn--active' : ''
            }`}
            onClick={changeLang(lang)}
          >
            {lang.toUpperCase()}
          </button>
        </React.Fragment>
      ))}
    </span>
  );
};

const ProfileNav = ({ user, locale, cancelSubs, changeLang, logout }) => {
  const isNotExpired = isSubsValid(user);
  const isRecurrent =
    user.subs && user.subs.recurrent && user.subs.recurrent.key;
  const isTrialAvailable = !isNotExpired && !user.subs.trial;

  const isEmployee = ['admin', 'teacher', 'manager', 'mentor'].includes(
    user.group
  );

  return (
    <NavDropdown
      eventKey={1}
      title={
        <React.Fragment>
          <StackedIcon icon="user" />
          &nbsp;
          {user.name || user.email}
        </React.Fragment>
      }
      id="user-dropdown"
      test-id="userNav-a-dropDown"
    >
      <MenuItem>
        <img
          className="avatar center-block"
          src={require(`../assets/images/avatars/${user.avatar}`)}
          alt="avatar"
        />
      </MenuItem>

      {!user.school && (
        <MenuItem className="hidden-nav-item">
          <b>
            {isNotExpired ? (
              user.subs.lessons > 0 ? (
                <span>
                  Осталось занятий:{' '}
                  <span test-id="userNav-span-countLessons">
                    {user.subs.lessons}
                  </span>
                </span>
              ) : (
                <React.Fragment>
                  <Translate value="nav.subscription.header" />
                  {formatDate(user.subs.valid, DATE_FORMAT)}
                </React.Fragment>
              )
            ) : (
              <Translate value="nav.subscription.inactive" />
            )}
          </b>

          <br />

          {!isRecurrent ? (
            isTrialAvailable ? (
              <button
                type="button"
                className="btn btn-orange btn-xs btn-block"
                onClick={() => browserHistory.push('/i/buy/trial')}
              >
                <Translate value="nav.subscription.try" />
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-orange btn-xs btn-block hidden-nav-item"
                onClick={() =>
                  browserHistory.push(`/i/buy/${user.subs.plan}`)
                }
              >
                <Translate value="nav.subscription.more" />
              </button>
            )
          ) : (
            <button
              type="button"
              className="btn btn-red btn-xs btn-block"
              onClick={cancelSubs}
            >
              <Translate value="nav.subscription.cancel" />
            </button>
          )}
        </MenuItem>
      )}

      {isEmployee && (
        <React.Fragment>
          <MenuItem divider />

          <MenuItem className="lang-switch-menu-item">
            <LangSwitch
              locale={locale}
              changeLang={changeLang}
              variant="dropdown"
            />
          </MenuItem>
        </React.Fragment>
      )}

      <MenuItem divider />

      {!user.school && (
        <React.Fragment>
          <LinkContainer to="/i/lessons-history" className="hidden-nav-item">
            <MenuItem eventKey={1.1}>
              <StackedIcon icon="list" />
              &nbsp;
              <Translate value="nav.lessons" />
            </MenuItem>
          </LinkContainer>
          <LinkContainer to="/i/payment-history" className="hidden-nav-item">
            <MenuItem eventKey={1.15}>
              <StackedIcon icon="usd" />
              &nbsp;
              <Translate value="nav.subs" />
            </MenuItem>
          </LinkContainer>
          <LinkContainer to="/i/buy/promocode" className="hidden-nav-item">
            <MenuItem eventKey={1.2}>
              <StackedIcon icon="percent" />
              &nbsp;
              <Translate value="nav.promocode" />
            </MenuItem>
          </LinkContainer>
        </React.Fragment>
      )}

      {isEmployee && (
        <LinkContainer to="/i/profile">
          <MenuItem eventKey={1.25}>
            <StackedIcon icon="user" />
            &nbsp;
            <Translate value="nav.profile" />
          </MenuItem>
        </LinkContainer>
      )}

      <LinkContainer to="/i/settings">
        <MenuItem eventKey={1.3}>
          <StackedIcon icon="wrench" />
          &nbsp;
          <Translate value="nav.settings" />
        </MenuItem>
      </LinkContainer>

      <MenuItem divider />

      <MenuItem eventKey={1.4} onSelect={logout}>
        <StackedIcon icon="sign-out" />
        &nbsp;
        <Translate value="auth.logout" />
      </MenuItem>
    </NavDropdown>
  );
};

const NavHeaderCaret = ({ text }) => (
  <span className="with-border">
    {text}
    &nbsp;
    <i className="fa fa-caret-down fa-lg" aria-hidden="true" />
  </span>
);

const isEditorPath = pathname =>
  pathname.startsWith('/i/gui/snap') || pathname.startsWith('/i/gui/code');

const UserNav = ({
  pathname,
  user,
  locale,
  selectedCourse,
  cancelSubs,
  logout,
  changeLang,
}) => (
  <Navbar
    collapseOnSelect
    fixedTop
    fluid
    className="progkids-default navbar-white"
  >
    <SubscribitionReminder user={user} location={pathname} />
    <Navbar.Header>
      <Navbar.Brand>
        <Link to="/i/all">
          <img
            className="navbar-logo pull-left"
            src={require('../assets/images/landings/logo.png')}
            alt="brand block progkids"
          />
          {user && user.school && (
            <img
              className="navbar-logo pull-left"
              src={require(`../assets/images/schools/${user.school}.png`)}
              alt={`logo ${user.school}`}
            />
          )}
        </Link>
      </Navbar.Brand>
      <Navbar.Toggle />
    </Navbar.Header>

    <Navbar.Collapse>
      {(!user ||
        ['admin', 'teacher', 'manager', 'mentor'].includes(user.group)) && (
        <Navbar.Text className="navbar-lang">
          <LangSwitch locale={locale} changeLang={changeLang} />
        </Navbar.Text>
      )}
      {!user && (
        <>
          <BaseNav>
            <LinkContainer to="/i/plan" className="link-gray nav-tariffs-link">
              <NavItem eventKey={3}>
                <Translate value="nav.tariffs" />
              </NavItem>
            </LinkContainer>
          </BaseNav>
          <Navbar.Form pullRight>
            |&nbsp;
            <button
              type="button"
              onClick={() => browserHistory.push('/i/auth')}
              className="btn btn-link"
            >
              <b>
                <Translate value="auth.login" />
              </b>
            </button>
            &nbsp;|
          </Navbar.Form>
        </>
      )}

      {user && (
        <>
          <BaseNav>
            {['admin', 'teacher'].includes(user.group) && (
              <NavDropdown
                eventKey={1}
                title={
                  <NavHeaderCaret text={I18n.t('admin.navigation.title')} />
                }
                noCaret={true}
                id="admin-dropdown"
              >
                {[
                  {
                    to: '/i/gui/homework',
                    title: I18n.t('admin.navigation.homework'),
                    groups: ['admin', 'teacher'],
                  },
                  {
                    to: '/i/gui/admin',
                    title: I18n.t('admin.navigation.courseEditor'),
                    groups: ['admin', 'author'],
                  },
                  {
                    to: '/i/gui/workinghours',
                    title: I18n.t('admin.navigation.workingHours'),
                    groups: ['admin', 'manager', 'mentor'],
                  },
                  {
                    to: '/i/gui/new-lessons',
                    title: I18n.t('admin.navigation.lessons'),
                    groups: ['admin', 'manager', 'mentor'],
                  },
                  {
                    to: '/i/gui/users',
                    title: I18n.t('admin.navigation.users'),
                    groups: ['admin', 'manager', 'mentor'],
                  },
                  {
                    to: '/i/gui/payments',
                    title: I18n.t('admin.navigation.payments'),
                    groups: ['admin', 'manager'],
                  },
                  {
                    to: '/i/gui/promocodes',
                    title: I18n.t('admin.navigation.promocodes'),
                    groups: ['admin'],
                  },
                  {
                    to: '/i/gui/subscriptions',
                    title: I18n.t('admin.navigation.subscriptions'),
                    groups: ['admin', 'manager'],
                  },
                  {
                    to: '/i/gui/subs',
                    title: I18n.t('admin.navigation.userSubscriptions'),
                    groups: ['admin', 'manager'],
                  },
                  {
                    to: '/i/gui/mails',
                    title: I18n.t('admin.navigation.mails'),
                    groups: ['admin', 'manager', 'mentor'],
                  },
                  {
                    to: '/i/gui/teachers',
                    title: 'Teachers',
                    groups: ['admin', 'manager', 'mentor'],
                  },
                  {
                    to: '/i/gui/playing',
                    title: I18n.t('admin.navigation.playing'),
                    groups: ['admin'],
                  },
                  {
                    to: '/i/gui/links',
                    title: 'links',
                    groups: ['admin'],
                  },
                  {
                    to: '/i/gui/external-api-logs',
                    title: 'External API logs',
                    groups: ['admin'],
                  },
                  {
                    to: '/i/gui/chat',
                    title: I18n.t('admin.navigation.inGameChatLogs'),
                    groups: ['admin'],
                  },
                  {
                    to: '/i/gui/attempts',
                    title: I18n.t('admin.navigation.attempts'),
                    groups: ['admin'],
                  },
                  {
                    to: '/i/gui/invitations',
                    title: I18n.t('admin.navigation.invitations'),
                    groups: ['admin'],
                  },
                  {
                    to: '/i/gui/mattermost',
                    title: 'Mattermost',
                    groups: ['admin', 'mentor'],
                  },
                  {
                    to: '/i/gui/extrasalary',
                    title: I18n.t('admin.navigation.salaryExtra'),
                    groups: ['admin'],
                  },
                  {
                    to: '/i/gui/salary',
                    title: I18n.t('admin.navigation.salary'),
                    groups: ['admin', 'manager', 'mentor'],
                  },
                  {
                    to: '/i/gui/docker',
                    title: 'Minecraft Servers',
                    groups: ['admin', 'manager'],
                  },
                ]
                  .filter(item => item.groups.includes(user.group))
                  .map(item => (
                    <LinkContainer key={item.to} to={item.to}>
                      <MenuItem eventKey={1.1}>{item.title}</MenuItem>
                    </LinkContainer>
                  ))}
              </NavDropdown>
            )}

            {!user.school && !isEditorPath(pathname) && (
              <React.Fragment>
                <LinkContainer to="/i/plan" className="link-gray nav-tariffs-link">
                  <NavItem eventKey={3}>
                    <Translate value="nav.tariffs" />
                  </NavItem>
                </LinkContainer>
                <LinkContainer to="/i/buy/promocode" className="link-gray nav-promocode-link">
                  <NavItem eventKey={4}>
                    <Translate value="nav.promocode" />
                  </NavItem>
                </LinkContainer>
              </React.Fragment>
            )}
          </BaseNav>
          <BaseNav pullRight>
            <ProfileNav
              user={user}
              locale={locale}
              logout={logout}
              cancelSubs={cancelSubs}
              changeLang={changeLang}
            />
          </BaseNav>
          {user.userType !== 'child' && !user.confirmed && (
            <Navbar.Form pullRight>
              <button
                type="button"
                onClick={() => browserHistory.push('/i/confirm')}
                className="btn btn-warning"
              >
                <Translate value="nav.confirm" />
              </button>
            </Navbar.Form>
          )}
          <BaseNav pullRight>
            {selectedCourse && isEditorPath(pathname) && (
              <LinkContainer
                to={`/i/${selectedCourse.type}/${selectedCourse._id}`}
              >
                <NavItem eventKey={0}>
                  <Translate value="nav.lessonback" />
                </NavItem>
              </LinkContainer>
            )}
          </BaseNav>
        </>
      )}
    </Navbar.Collapse>
  </Navbar>
);

UserNav.propTypes = {
  user: PropTypes.any,
  selectedCourse: PropTypes.any,
  changeLang: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
};

UserNav.defaultProps = {
  user: false,
};

const mapStateToProps = state => ({
  user: state.auth.user,
  locale: state.i18n.locale,
  selectedCourse: state.courses.selectedCourse,
});

const mapDispatchToProps = dispatch => ({
  logout: () => dispatch(actionLogout()),
  changeLang: lang => () => dispatch(actionChangeLang(lang)),
  cancelSubs: () => dispatch(actionCancelRecurrent()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserNav);
